// Settings: who you are, which station you are at, how the app looks, and how
// to sign out.
//
// Nothing about the work lives here any more. The flowline is picked on the
// screens that show it, and an operator's own alerts are on the Alerts tab —
// a setting that duplicates a screen is a second place for it to be wrong.
//
// One way out: sign out of the device. There is no "switch operator": a PIN
// device is one person's phone, so there is nobody to switch to.

import { api } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Flag, Icon } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Sheet';
import { AvatarTile, SectionLabel } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';
import { LANGS, type Lang } from '@/i18n/strings';
import { useWaitingCounts, useWorkcentres } from '@/queries';
import { useStore } from '@/store';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { ACCENT_NAMES, type AccentName, type ThemeMode } from '@/theme/palette';

export function SettingsSheet({
  open,
  onClose,
  onPickStation,
}: {
  open: boolean;
  onClose: () => void;
  /** Opens the searchable station list. */
  onPickStation: () => void;
}) {
  const t = useT();
  const operator = useStore((s) => s.operator);
  const workcentreId = useStore((s) => s.workcentreId);
  const flowlineId = useStore((s) => s.flowlineId);
  const resetOperator = useStore((s) => s.resetOperator);
  const resetJob = useStore((s) => s.resetJob);
  const setAuthStage = useStore((s) => s.setAuthStage);

  // One selector per value: an object literal would be a new reference every
  // render, which Zustand v5 treats as a state change and loops on.
  const theme = useStore((s) => s.userTheme);
  const accent = useStore((s) => s.userAccent);
  const language = useStore((s) => s.language);
  const setTheme = useStore((s) => s.setTheme);
  const setAccent = useStore((s) => s.setAccent);
  const setLanguage = useStore((s) => s.setLanguage);
  // Deliberately ignores installDismissed: dismissing a banner means "stop
  // asking", not "never let me do this". Settings is where you go to look.
  // Once installed there is nothing to do, so the section goes rather than
  // staying to say so.
  const { method: installMethod, installed, install } = useInstallPrompt();

  const workcentres = useWorkcentres();
  const counts = useWaitingCounts(flowlineId);

  const wcs = workcentres.data ?? [];
  const currentStation = wcs.find((w) => w.id === workcentreId);

  const signOutDevice = async () => {
    await api.logout({ full: true }).catch(() => {});
    resetJob();
    resetOperator();
    setAuthStage('chooser');
    onClose();
    window.location.reload();
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      variant="tall"
      title={t.settings}
      hideTitle
      // Every change here applies as it is tapped, so this is Close and not
      // Save — but a sheet with no way out except the backdrop is one people
      // sit in front of, wondering.
      footer={
        <Button variant="ink" size="md" onClick={onClose}>
          {t.closeT}
        </Button>
      }
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <AvatarTile name={operator?.name ?? '?'} size={48} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: "700 16px 'Space Grotesk',sans-serif" }}>{operator?.name ?? '—'}</div>
          <div style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)' }}>
            {operator?.jobTitle ?? t.station}
          </div>
        </div>
      </div>

      <SectionLabel>{t.station}</SectionLabel>
      <button
        type="button"
        className="press-row"
        onClick={onPickStation}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '14px 14px',
          borderRadius: 13,
          background: 'var(--surface2)',
          border: '1.5px solid var(--line)',
          marginBottom: 20,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <span style={{ display: 'block', font: "600 14px 'Space Grotesk',sans-serif" }}>
            {currentStation?.name ?? 'Choose a station'}
          </span>
          <span style={{ display: 'block', font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 2 }}>
            {currentStation
              ? `${counts.data?.[currentStation.id] ?? 0} ${t.waiting}${currentStation.isEol ? ' · End of line' : ''}`
              : `${wcs.length} stations`}
          </span>
        </span>
        <span aria-hidden="true" style={{ color: 'var(--sub)' }}>›</span>
      </button>

      {installMethod !== 'none' && !installed && (
        <>
          <SectionLabel>{t.installTitle}</SectionLabel>
          {installMethod === 'ios-manual' ? (
            <p style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)', margin: '0 0 18px' }}>
              {t.installIosHow}
            </p>
          ) : (
            <button
              type="button"
              className="press-row"
              onClick={() => void install()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '14px',
                borderRadius: 13,
                background: 'var(--surface2)',
                border: '1.5px solid var(--line)',
                marginBottom: 20,
                minHeight: 44,
              }}
            >
              <Icon name="file" size={17} />
              <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <span style={{ display: 'block', font: "600 14px 'Space Grotesk',sans-serif" }}>
                  {t.installAction}
                </span>
                <span style={{ display: 'block', font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 2 }}>
                  {t.installWhy}
                </span>
              </span>
              <span aria-hidden="true" style={{ color: 'var(--sub)' }}>›</span>
            </button>
          )}
        </>
      )}

      <SectionLabel>{t.language}</SectionLabel>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {LANGS.map((l: Lang) => (
          <Chip key={l} kind="select" selected={language === l} onClick={() => setLanguage(l)}>
            <Flag lang={l} /> {l}
          </Chip>
        ))}
      </div>

      <SectionLabel>{t.appearance}</SectionLabel>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {(['light', 'dark', 'system'] as ThemeMode[]).map((m) => (
          <Chip key={m} kind="select" selected={theme === m} onClick={() => setTheme(m)}>
            {m === 'light' ? t.light : m === 'dark' ? t.dark : t.systemT}
          </Chip>
        ))}
      </div>

      <SectionLabel>{t.accentT}</SectionLabel>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        {ACCENT_NAMES.map((a: AccentName) => (
          <Chip key={a} kind="select" selected={accent === a} onClick={() => setAccent(a)}>
            {a}
          </Chip>
        ))}
      </div>

      <div style={{ display: 'grid', gap: 10, marginTop: 6 }}>
        <Button variant="dangerGhost" size="md" onClick={signOutDevice} icon={<Icon name="logout" size={16} />}>
          {t.signOutDevice}
        </Button>
      </div>
    </Sheet>
  );
}

